import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { PrismEditor, PrismLayoutMode } from '@lender/prism-editor';
import { Box, useTheme } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PagelessToolbarPreset } from './PrismPagelessEditor.constants';
import { getRootSx } from './PrismPagelessEditor.styles';
import { getReadOnlyToolbar, getSurfaceTheme } from './PrismPagelessEditor.utils';

const PrismPagelessEditor = forwardRef(
  (
    {
      value = '',
      onChange = _.noop,
      toolbarPreset = PagelessToolbarPreset.COMPACT,
      readOnly = false,
      disabled = false,
      autoFocus = false,
      error = false,
      hasFrame = true,
      minHeight,
      fillHeight = false,
      toolbarContainer = null,
      renderWidgetEditor,
      resolveDynamicFieldLabel,
      signatureLabels,
      placeholder,
      ariaLabel,
      className,
      'data-testid': dataTestId,
    },
    ref,
  ) => {
    const theme = useTheme();
    const prismRef = useRef(null);
    const [initialValue] = useState(() => value ?? '');
    const lastHtmlRef = useRef(initialValue);
    const onChangeRef = useRef(onChange);
    const autoFocusRef = useRef(autoFocus);
    const pendingInsertsRef = useRef([]);
    const isReadOnly = readOnly || disabled;
    const isReadOnlyRef = useRef(isReadOnly);

    const onEditorChange = useCallback(html => {
      if (html === lastHtmlRef.current) {
        return;
      }

      lastHtmlRef.current = html;
      onChangeRef.current(html);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        // Prism drops an insert into a read-only field; a host busy loading the content
        // (a snippet) has the field disabled meanwhile, so the insert waits for it.
        insertHtml: (html, options) => {
          if (isReadOnlyRef.current) {
            pendingInsertsRef.current.push([html, options]);

            return;
          }

          prismRef.current?.insertHtml(html, options);
        },
        focus: () => prismRef.current?.focus(),
        getDocumentHtml: () => prismRef.current?.getDocumentHtml() ?? '',
      }),
      [],
    );

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      isReadOnlyRef.current = isReadOnly;

      if (isReadOnly) {
        return;
      }

      const pendingInserts = pendingInsertsRef.current;
      pendingInsertsRef.current = [];
      pendingInserts.forEach(([html, options]) => prismRef.current?.insertHtml(html, options));
    }, [isReadOnly]);

    // A value the host sets itself (a reset after saving) replaces the document in place; a new
    // `initialValue` would remount the editor.
    useEffect(() => {
      const html = value ?? '';

      if (html === lastHtmlRef.current) {
        return;
      }

      lastHtmlRef.current = html;
      prismRef.current?.setDocumentHtml(html);
    }, [value]);

    // Focused once on mount, as a native field's autoFocus attribute does.
    useEffect(() => {
      if (autoFocusRef.current) {
        prismRef.current?.focus();
      }
    }, []);

    return (
      <Box
        className={className}
        data-testid={dataTestId}
        sx={getRootSx(theme, { error, hasFrame, fillHeight })}
      >
        <PrismEditor
          ariaLabel={ariaLabel}
          fallbackFontsBaseUrl={process.env.PRISM_FALLBACK_FONTS_URL}
          fillHeight={fillHeight}
          initialValue={initialValue}
          layoutMode={PrismLayoutMode.Pageless}
          minHeight={minHeight}
          placeholder={placeholder}
          readOnly={isReadOnly}
          readOnlyToolbar={getReadOnlyToolbar({ readOnly, disabled })}
          ref={prismRef}
          renderWidgetEditor={renderWidgetEditor}
          resolveDynamicFieldLabel={resolveDynamicFieldLabel}
          signatureLabels={signatureLabels}
          theme={getSurfaceTheme(theme)}
          toolbarContainer={toolbarContainer}
          toolbarPreset={toolbarPreset}
          onChange={onEditorChange}
        />
      </Box>
    );
  },
);

PrismPagelessEditor.propTypes = {
  value: PropTypes.string,
  toolbarPreset: PropTypes.oneOf(Object.values(PagelessToolbarPreset)),
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  error: PropTypes.bool,
  hasFrame: PropTypes.bool,
  minHeight: PropTypes.number,
  fillHeight: PropTypes.bool,
  toolbarContainer: PropTypes.instanceOf(Element),
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  'data-testid': PropTypes.string,
  signatureLabels: PropTypes.object,
  renderWidgetEditor: PropTypes.func,
  resolveDynamicFieldLabel: PropTypes.func,
  onChange: PropTypes.func,
};

export default PrismPagelessEditor;
